import {useQueryClient} from '@github-ui/react-query'
import {getHighlightedEvent, isEventHighlighted} from '@github-ui/timeline-items/HighlightedEvent'
import {useCallback, useMemo, useState} from 'react'

import {
  type TimelineIssue,
  type TimelineItemsConnection,
  type TimelineItemsPageParams,
  timelineQueryOptions,
} from './fetch-timeline-items'
import type {IssueTimelineItemFragment} from './IssueTimelineItem'
import {
  assembleTimelineItems,
  countItemsRemaining,
  dedupeItems,
  isHighlightReady,
  subtractItems,
  type TimelineCounters,
  type TimelineSlot,
} from './timeline-assembly'
import {asTimelineItem} from './timeline-item-parity'
import type {UseTimelineItemsCoreResult} from './use-timeline-items-core'
import {type TimelineSection, useLoadedPages, useTimelineSections} from './use-timeline-sections'

export type UseTimelineItemsTanstackProps = {
  owner: string
  repo: string
  number: number
  highlightedEvent?: string
  page?: number
  /** Matches the Relay path's first page size so both produce the same initial view. */
  pageSize?: number
}

type LoadMoreOptions = {onComplete?: () => void}

const connectionOf = (issue: TimelineIssue | undefined) => issue?.timelineItems ?? undefined

const isPresent = <T>(value: T | undefined): value is T => value !== undefined

const DEFAULT_PAGE_SIZE = 15
const FOCUS_NEIGHBOR_COUNT = 3
/** The endpoint's own cap on a single page. */
const MAX_SERVER_PAGE_SIZE = 150

function edgeNodes(connection: TimelineItemsConnection | undefined): IssueTimelineItemFragment[] {
  if (!connection) return []

  return (connection.edges ?? []).flatMap(edge => (edge?.node ? [asTimelineItem(edge.node)] : []))
}

/**
 * # How the timeline is read
 *
 * The timeline is too long to fetch whole, so it is read as a few **sections** with gaps between
 * them. Each section is one read of the endpoint. `use-timeline-sections.ts` performs the reads;
 * this file decides what to ask for and stitches the answers into one list of rows.
 *
 * ## The sections, and how each one grows
 *
 * **front** -- the oldest comments, where the timeline starts. Opens with `{first: 15}`
 * (`DEFAULT_PAGE_SIZE`, overridable by the caller). "Load more" never enlarges it: it adds a
 * separate section reading `{first: N, after: <the last row it holds>}`, rendered below it.
 *
 * **back** -- the newest comments, 15 of them. It exists only once the front section reports there
 * is more beyond it, which is what `frontSectionRead` carries: the two ends are read in sequence.
 * "Load more" upward adds a section reading `{last: N, before: <the first row it holds>}`.
 *
 * **highlight** -- a group around a permalinked comment: the comment itself plus
 * `FOCUS_NEIGHBOR_COUNT` either side, so 7 rows, with its own "load more" on each side.
 *
 * A "load more" section holds 150 rows (`VALUES.timeline.pageSize`), or the whole remainder when
 * fewer than that are left, so a gap of any ordinary size closes in one activation.
 *
 * ## The anchor
 *
 * `after: X` returns the rows *after* the one `X` points at, so pinning the back section needs the
 * cursor of the row above its first -- the 16th from the end. That cursor is the **anchor**, and
 * every read the section makes is `{last: MAX_PAGE_SIZE, after: anchor}`.
 *
 * On a short timeline that row is one the front section already read, so its cursor is taken from
 * there. Otherwise the section reads `{last: 16}` once to fetch it. The extra row is never
 * rendered: it belongs to neither section, and the "N remaining items" marker is what accounts for
 * it.
 *
 * ## What happens when a comment is posted
 *
 * The subscription invalidates every section, so all of them re-read.
 *
 * **On a timeline with a gap**, the new comment sorts after the anchor, so the back section's read
 * is unchanged and simply returns one row more than it did. Nothing is resized and no cache key
 * moves. The marker does not move either: the timeline grew by one and so did the rows on screen.
 *
 * **On a timeline the front section held all of**, the 16th row is the first `{first: 15}` cannot
 * return, so the front reports more beyond it and the back section opens. It anchors on the row
 * above its first and covers everything from there, which on a timeline this short is the rest of
 * it -- so the reader still sees an unbroken list, now read as two sections rather than one.
 *
 * Neither case sizes a read from the timeline's length. The anchored read asks for the endpoint's
 * cap from the start, so ten comments arriving at once are absorbed by the request that absorbs
 * one. Growth past `MAX_PAGE_SIZE` rows beyond the anchor falls outside the section and becomes an
 * ordinary gap.
 */

/**
 * TanStack implementation of the timeline, selected by `issue_viewer_paved_path`. Returns
 * the same shape as the Relay hook, so the flag flips at a single call site and the rendering path
 * never learns which source it read from.
 *
 * Like the Relay path it reads two sections -- oldest and newest -- with a "load more" gap between
 * them, plus a third around a deep-linked event. Every one of them, and every page opened with
 * "load more", is a section in one list: params in, a result out.
 */
export function useTimelineItemsTanstack({
  owner,
  repo,
  number,
  highlightedEvent,
  page,
  pageSize = DEFAULT_PAGE_SIZE,
}: UseTimelineItemsTanstackProps): UseTimelineItemsCoreResult {
  const queryClient = useQueryClient()
  const highlight = useMemo(() => getHighlightedEvent(highlightedEvent), [highlightedEvent])
  const focusText = highlight ? `${highlight.prefix}-${highlight.id}` : undefined

  const queryOptions = useCallback(
    (params: TimelineItemsPageParams) => timelineQueryOptions(owner, repo, number, params),
    [owner, repo, number],
  )

  const loadedPages = useLoadedPages(focusText)

  // What the front section's last read reported, which is what tells the back section it has rows
  // to cover: the two ends are read in sequence.
  const [frontSectionRead, setFrontSectionRead] = useState<{hasMoreAfter: boolean}>({hasMoreAfter: false})
  const [backAnchor, setBackAnchor] = useState<string>()

  // `page` renders one explicit page with no gap, so it has no newest-items section. From
  // `hasNextPage` rather than `totalCount`, which a pending write moves.
  const backEnabled = !page && frontSectionRead.hasMoreAfter

  // `last: N` alone is relative to the end, so it slides off whatever the reader was looking at as
  // rows arrive. Anchoring pins where the section starts instead. The first read asks for one row
  // more than the section shows, purely to get the cursor of the row above it; from then on
  // `after` that cursor returns the section itself. Still read from the end, so past the page cap
  // it gives up its oldest rows rather than the newest.
  const backParams: TimelineItemsPageParams = useMemo(
    () => (backAnchor ? {last: MAX_SERVER_PAGE_SIZE, after: backAnchor} : {last: pageSize + 1}),
    [backAnchor, pageSize],
  )

  // In the order they render: the oldest rows, then the permalink window sitting in the gap, then
  // the newest. `select` reads back along this order, so it is the section order, not a listing.
  const sections = useMemo(
    (): TimelineSection[] => [
      {role: 'front', params: {first: pageSize}, isBase: true},
      ...loadedPages.front.map((params): TimelineSection => ({role: 'front', params, alreadyFetched: true})),
      ...loadedPages.highlight.filter(section => section.role === 'highlight-before'),
      ...(focusText
        ? [
            {
              role: 'highlight' as const,
              params: {focusText, first: 1, focusNeighborCount: FOCUS_NEIGHBOR_COUNT},
              isBase: true,
            },
          ]
        : []),
      ...loadedPages.highlight.filter(section => section.role === 'highlight-after'),
      // Pages read backward from the newest end are older than it, so they render before it.
      ...loadedPages.back.map((params): TimelineSection => ({role: 'back', params, alreadyFetched: true})),
      {role: 'back', params: backParams, isBase: true, enabled: backEnabled},
    ],
    [pageSize, backParams, backEnabled, focusText, loadedPages.front, loadedPages.back, loadedPages.highlight],
  )

  const {results, loading, select, baseIndex} = useTimelineSections({owner, repo, number, sections})

  const fetchedFrontConnection = connectionOf(results[baseIndex('front')])

  const fetchedHasMore = Boolean(fetchedFrontConnection?.pageInfo?.hasNextPage)

  if (fetchedFrontConnection && fetchedHasMore !== frontSectionRead.hasMoreAfter) {
    setFrontSectionRead({hasMoreAfter: fetchedHasMore})
  }

  const backBaseIndex = baseIndex('back')
  const backBaseConnection = connectionOf(results[backBaseIndex])

  // The anchor sits one row above the newest `pageSize`, and the front section's rows carry their
  // own cursors -- so on a timeline short enough for the anchor to be one of them, it is already in
  // hand and the probe read is skipped entirely.
  const anchorPosition = (fetchedFrontConnection?.totalCount ?? 0) - pageSize - 1
  const frontEdges = fetchedFrontConnection?.edges
  const anchorFromFront =
    anchorPosition >= 0 && frontEdges && anchorPosition < frontEdges.length
      ? frontEdges[anchorPosition]?.cursor
      : undefined

  if (!backAnchor && anchorFromFront) setBackAnchor(anchorFromFront)
  else if (backBaseConnection && !backAnchor && backBaseConnection.pageInfo?.startCursor) {
    setBackAnchor(backBaseConnection.pageInfo.startCursor)
  }

  const connections = useMemo(() => results.map(result => connectionOf(result)), [results])

  const frontPages = useMemo(() => select(connections, 'front').filter(isPresent), [select, connections])

  const frontItems = useMemo(() => dedupeItems(frontPages.flatMap(edgeNodes)), [frontPages])

  const totalItemCount = Math.max(fetchedFrontConnection?.totalCount ?? 0, 0)

  const backPages = useMemo(
    () => (backEnabled ? select(connections, 'back').filter(isPresent) : []),
    [backEnabled, select, connections],
  )

  const backIsLoading = loading[baseIndex('back')] ?? false

  const backItems = useMemo(
    () => subtractItems(dedupeItems(backPages.flatMap(edgeNodes)), frontItems),
    [backPages, frontItems],
  )

  const loadedItems = useMemo(() => [...frontItems, ...backItems], [frontItems, backItems])

  const highlightIsPreloaded = useMemo(
    () => (highlight ? loadedItems.some(event => isEventHighlighted(event, highlight)) : false),
    [loadedItems, highlight],
  )

  const highlightIndex = baseIndex('highlight')
  const highlightData = connections[highlightIndex]

  const highlightBefore = useMemo(
    () => select(connections, 'highlight-before').filter(isPresent),
    [select, connections],
  )
  const highlightAfter = useMemo(() => select(connections, 'highlight-after').filter(isPresent), [select, connections])

  const highlightItems = useMemo(() => {
    if (!highlightData) return []

    return dedupeItems([
      ...highlightBefore.flatMap(edgeNodes),
      ...edgeNodes(highlightData),
      ...highlightAfter.flatMap(edgeNodes),
    ])
  }, [highlightData, highlightBefore, highlightAfter])

  // Each item paged in ahead of the focus moves the section's start that much earlier.
  const prependedHighlightCount = useMemo(
    () => highlightBefore.reduce((sum, connection) => sum + edgeNodes(connection).length, 0),
    [highlightBefore],
  )

  const highlightStartPosition = Math.max(
    (connectionOf(results[highlightIndex])?.beforeFocusCount ?? 0) - prependedHighlightCount,
    0,
  )

  const uniqueHighlightItems = useMemo(
    () => (highlightItems.length > 0 ? subtractItems(highlightItems, loadedItems) : []),
    [highlightItems, loadedItems],
  )

  const hasLazyLoadedHighlights = uniqueHighlightItems.length > 0

  const timelineCounters: TimelineCounters = useMemo(
    () => ({
      frontItems: frontItems.length,
      backItems: backItems.length,
      highlightItems: uniqueHighlightItems.length,
      highlightStartPosition,
    }),
    [frontItems.length, backItems.length, uniqueHighlightItems.length, highlightStartPosition],
  )

  const {front: itemsRemainingFront, back: itemsRemainingBack} = countItemsRemaining({
    hasLazyLoadedHighlights,
    timelineCounters,
    totalItemCount,
    frontItemCount: frontItems.length,
    backItemCount: backItems.length,
  })

  // One request per activation; the endpoint clamps the page size it is given.
  const loadPages = useCallback(
    (
      count: number,
      /** The page to continue from: the one furthest in the direction being read. */
      from: TimelineItemsConnection | undefined,
      paramsFor: (from: TimelineItemsConnection, size: number) => TimelineItemsPageParams | null,
      apply: (params: TimelineItemsPageParams) => void,
      options?: LoadMoreOptions,
    ) => {
      void (async () => {
        const params = from && paramsFor(from, count)
        // `LoadMore` clears its own loading state only in `onComplete`, so every path has to reach
        // it -- including this one, or the control stays disabled with nothing in flight.
        if (!params) return options?.onComplete?.()

        try {
          const connection = connectionOf(await queryClient.fetchQuery(queryOptions(params)))
          // The rows are already in the cache; holding the params is what keeps the page live.
          if (connection && (connection.edges ?? []).length > 0) apply(params)
        } catch {
          // Keep the marker in place so the read can be retried.
        }

        options?.onComplete?.()
      })()
    },
    [queryClient, queryOptions],
  )

  const loadMoreFrontItems = useCallback(
    (count: number, options?: LoadMoreOptions) => {
      loadPages(
        count,
        frontPages.at(-1),
        (from, size) =>
          from.pageInfo?.hasNextPage && from.pageInfo.endCursor ? {first: size, after: from.pageInfo.endCursor} : null,
        loadedPages.addFront,
        options,
      )
    },
    [loadPages, frontPages, loadedPages.addFront],
  )

  const loadMoreBackItems = useCallback(
    (count: number, options?: LoadMoreOptions) => {
      loadPages(
        count,
        backPages.at(0),
        // Not `hasPreviousPage`: the resolver only counts that when `last` slices what the cursors
        // selected, so an anchored section reports false however many rows precede it.
        (from, size) => (from.pageInfo?.startCursor ? {last: size, before: from.pageInfo.startCursor} : null),
        loadedPages.addBack,
        options,
      )
    },
    [loadPages, backPages, loadedPages.addBack],
  )

  const loadBeforeHighlight = useCallback(
    (count: number, options?: LoadMoreOptions) => {
      if (!highlightData) return options?.onComplete?.()

      // Continues from the last page read, not from the highlight section's own cursor, which
      // would refetch the same page on every activation.
      loadPages(
        count,
        highlightBefore.at(0) ?? highlightData,
        (from, size) =>
          from.pageInfo?.hasPreviousPage && from.pageInfo.startCursor
            ? {last: size, before: from.pageInfo.startCursor}
            : null,
        loadedPages.addHighlight('before'),
        options,
      )
    },
    [loadPages, highlightData, highlightBefore, loadedPages],
  )

  const loadAfterHighlight = useCallback(
    (count: number, options?: LoadMoreOptions) => {
      if (!highlightData) return options?.onComplete?.()

      loadPages(
        count,
        highlightAfter.at(-1) ?? highlightData,
        (from, size) =>
          from.pageInfo?.hasNextPage && from.pageInfo.endCursor ? {first: size, after: from.pageInfo.endCursor} : null,
        loadedPages.addHighlight('after'),
        options,
      )
    },
    [loadPages, highlightData, highlightAfter, loadedPages],
  )

  const slots: TimelineSlot[] | null = useMemo(
    () =>
      assembleTimelineItems({
        frontItems,
        backItems,
        emptyBackItems: [],
        uniqueLoadedHighlightItems: uniqueHighlightItems,
        totalItemCount,
        itemsRemainingFront,
        itemsRemainingBack,
        hasLazyLoadedHighlights,
        loadingInitialBackItems: backEnabled && backIsLoading,
        page,
      }),
    [
      frontItems,
      backItems,
      uniqueHighlightItems,
      totalItemCount,
      itemsRemainingFront,
      itemsRemainingBack,
      hasLazyLoadedHighlights,
      backEnabled,
      backIsLoading,
      page,
    ],
  )

  const timelineItems = useMemo(
    () =>
      slots?.map(slot => {
        if (slot.type !== 'load-marker') return slot

        const {type: _marker, ...rest} = slot
        return slot.position === 'top'
          ? {
              ...rest,
              type: 'load' as const,
              loadFromTop: loadMoreFrontItems,
              loadFromBottom: hasLazyLoadedHighlights ? loadBeforeHighlight : loadMoreBackItems,
            }
          : {...rest, type: 'load' as const, loadFromTop: loadAfterHighlight, loadFromBottom: loadMoreBackItems}
      }) ?? null,
    [slots, hasLazyLoadedHighlights, loadMoreFrontItems, loadMoreBackItems, loadBeforeHighlight, loadAfterHighlight],
  )

  return {
    timelineItems,
    totalItemCount,
    highlightedReady: isHighlightReady(totalItemCount, backItems.length, timelineCounters, highlightIsPreloaded),
  }
}
